import { normalize, schema } from "normalizr";

import { currencyList, maticId, postAccess } from "@const/postManagement";
import { removeEmptyKeys } from "./common";
import { isAudioFile, isImageFile, isVideoFile } from "./files";
import { toCents } from "./price";
import { imageResizedKey, imageSubscriptionResizedKey } from "@const/feed";
import makeScheduleDate from "@helpers/makeScheduleDate";

/**
 * Create post model
 * @param {object} model
 * @param isScheduled
 * @param filesIds
 */
export const createPostModel = ({ model, isScheduled, filesIds }) => {
  const {
    type,
    files = [],
    title,
    description,
    isOneTime,
    isWithSub,
    priceType,
    price,
    priceTypeLabel,
    accessType,
    subscriptionIds,
    consentFormIds,
    poster,
    socialType,
    youtubeVideoLink,
    socialContent,
    twitchAccessToken,
    twitchVideoLink,
    scheduleDate,
    scheduleTime,
    scheduleTimezone,
    streamId,
    ...restOfModel
  } = model;

  const postModel = {
    type,
    accessType,
    title: title?.trim(),
    description: description?.trim(),
    streamId,
    ...restOfModel,
  };
  if (streamId) {
    postModel.files = [
      {
        fileId: postModel.streamRecordingId,
        previewId: postModel.coverImageId,
      },
    ];
    delete postModel.coverImageId;
    delete postModel.streamRecordingId;
  } else {
    postModel.files = files.map(({ uuid }) => ({
      fileId: filesIds[uuid].fileId,
      previewId: filesIds[uuid]?.poster?.fileId,
    }));
  }

  if (isScheduled && scheduleDate && scheduleTime && scheduleTimezone) {
    const { scheduledDate, scheduledTimeZoneName } = makeScheduleDate(
      scheduleTime,
      model.scheduleDate,
      scheduleTimezone,
    );
    postModel.scheduledDate = scheduledDate;
    postModel.scheduledTimeZoneName = scheduledTimeZoneName;
  }

  if (accessType === postAccess.paid && isOneTime) {
    const isInUSD = +priceType === +currencyList[0].value;

    postModel.price = isInUSD ? toCents(price) : price;
    postModel.priceType = priceType;
  }

  // todo: add select subscription logic
  if (accessType === postAccess.paid && isWithSub) {
    postModel.subscriptionIds = subscriptionIds;
  }
  if (consentFormIds?.length) {
    postModel.consentFormIds = consentFormIds;
  }
  if (postModel.files.length === 0) {
    delete postModel.files;
  }

  return removeEmptyKeys(postModel);
};

export const normalizePostArray = (originalPost) => {
  const authorSchema = new schema.Entity("authors");
  const subscriptionSchema = new schema.Entity(
    "subscriptions",
    {},
    {
      processStrategy: (value, parent) => ({ ...value, author: parent.author }),
    },
  );
  const postsSchema = new schema.Entity("posts", {
    author: authorSchema,
    subscriptions: [subscriptionSchema],
  });

  const posts = new schema.Array(postsSchema);

  return normalize(originalPost, posts);
};

export const normalizePost = (originalPost) => {
  const authorSchema = new schema.Entity("authors");
  const subscriptionSchema = new schema.Entity(
    "subscriptions",
    {},
    {
      processStrategy: (value, parent) => ({ ...value, author: parent.author }),
    },
  );
  const postsSchema = new schema.Entity("posts", {
    author: authorSchema,
    subscriptions: [subscriptionSchema],
  });

  return normalize(originalPost, postsSchema);
};

export const isImageUrl = (url) => /\.(jpg|jpeg|png)$/.test(url);

export const createEditPostModel = async (
  {
    title,
    price,
    priceType,
    accessType,
    description,
    subscriptionIds,
    consentFormIds,
    isOneTime,
    isWithSub,
    scheduleDate,
    scheduleTime,
    scheduleTimezone,
    contentType,
    socialType,
    files = [],
    type,
    poster,
    streamId,
    isLiveStreamChatDisplayed,
  },
  isPublished,
) => {
  const model = {
    title,
    accessType,
    description,
    contentType,
    socialType,
    files,
    isLiveStreamChatDisplayed,
  };
  if (!files.length || streamId) {
    delete model.files;
  }
  const isPaid = +accessType === +postAccess.paid;

  if (isPaid && isOneTime) {
    const isMatic = priceType == maticId;

    model.priceType = priceType;
    model.price = isMatic ? price : toCents(price);
  }

  if (isPaid && isWithSub) {
    model.subscriptionIds = subscriptionIds;
  }

  if (consentFormIds.length) {
    model.consentFormIds = consentFormIds;
  }

  if (!isPublished && scheduleDate && scheduleTime && scheduleTimezone) {
    const { scheduledDate, scheduledTimeZoneName } = makeScheduleDate(
      scheduleTime,
      model.scheduledDate,
      model.scheduledTimeZoneName,
    );
    model.scheduledDate = scheduledDate;
    model.scheduledTimeZoneName = scheduledTimeZoneName;
  }

  model.isPublished = isPublished;

  return removeEmptyKeys(model);
};

export function countFiles(files) {
  // Initialize counters for each file type
  const fileTypeCounts = {
    videos: 0,
    audios: 0,
    images: 0,
  };

  // Define MIME type patterns for each file type

  // Iterate through each file and update the respective counters
  files.forEach((file) => {
    if (isVideoFile(file)) {
      fileTypeCounts.videos++;
    } else if (isAudioFile(file)) {
      fileTypeCounts.audios++;
    } else if (isImageFile(file)) {
      fileTypeCounts.images++;
    }
  });

  return fileTypeCounts;
}

export const getCurrentSrc = (file, showOriginalFiles) => {
  if (showOriginalFiles) {
    return file?.link;
  }
  return file?.resizedImages?.[imageResizedKey]?.link || file?.link;
};

export const getSubscriptionCurrentSrc = (file) => {
  return file?.resizedImages?.[imageSubscriptionResizedKey]?.link || file?.link;
};
